import { useState, useEffect } from 'react'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { FormField } from '@/components/ui/form'
import { Modal } from '@/components/ui/modal'

export interface RoadmapStepEditData {
  id: number
  title: string
  description: string
  date: string
  visibleToClient: boolean
}

export interface EditRoadmapStepModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (data: RoadmapStepEditData) => void | Promise<void>
  isLoading?: boolean
  step: RoadmapStepEditData | null
}

export function EditRoadmapStepModal({
  open,
  onOpenChange,
  onSubmit,
  isLoading = false,
  step,
}: EditRoadmapStepModalProps) {
  const [formData, setFormData] = useState<RoadmapStepEditData>({
    id: 0,
    title: '',
    description: '',
    date: '',
    visibleToClient: true,
  })

  // Update form data when step prop changes
  useEffect(() => {
    if (step) {
      setFormData({
        id: step.id,
        title: step.title,
        description: step.description,
        date: step.date,
        visibleToClient: step.visibleToClient,
      })
    }
  }, [step])

  const handleInputChange = (field: keyof RoadmapStepEditData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handleSubmit = async () => {
    if (!formData.title.trim() || !formData.date.trim()) {
      return
    }

    await onSubmit(formData)
  }

  const handleCancel = () => {
    onOpenChange(false)
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      size="md"
      hideHeader={true}
      contentClassName="border-4 border-cyan-400 rounded-2xl shadow-2xl"
      footer={
        <div className="flex gap-3 pt-4 justify-start">
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={isLoading}
          >
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            isLoading={isLoading}
            className="bg-gradient-to-r from-cyan-400 to-cyan-500 text-white hover:from-cyan-500 hover:to-cyan-600 shadow-md"
          >
            Enregistrer les modifications
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        <div className="bg-white border-b border-gray-200 px-6 py-4 -mx-6 -mt-6 mb-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Modifier l'étape</h2>
            <p className="text-sm text-gray-500 mt-1">
              Modifiez les informations de cette étape de roadmap
            </p>
          </div>
        </div>

        <FormField label="Titre de l'étape" required>
          <Input
            type="text"
            value={formData.title}
            onChange={(e) => handleInputChange('title', e.target.value)}
            placeholder="Ex: Kickoff, Livraison MVP, Tests..."
            className="bg-gray-50 border-gray-200 focus:ring-cyan-400 focus:border-transparent"
          />
        </FormField>

        <FormField label="Description (optionnel)">
          <Textarea
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            placeholder="Décrivez cette étape..."
            rows={4}
            className="bg-gray-50 border-gray-200 focus:ring-cyan-400 focus:border-transparent resize-none"
          />
        </FormField>

        <FormField label="Date" required>
          <Input
            type="date"
            value={formData.date}
            onChange={(e) => handleInputChange('date', e.target.value)}
            className="bg-gray-50 border-gray-200 focus:ring-cyan-400 focus:border-transparent"
          />
        </FormField>

        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-700">
              Visible par le client
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Le client pourra voir cette étape dans la roadmap du projet
            </p>
          </div>
          <button
            type="button"
            onClick={() => handleInputChange('visibleToClient', !formData.visibleToClient)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              formData.visibleToClient ? 'bg-cyan-500' : 'bg-gray-300'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                formData.visibleToClient ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </div>
    </Modal>
  )
}

